<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-sidebar">
        <div class="sidebar-content">
          <div class="app-logo">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
              <path d="M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.36 8-6.14 8-10.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-3.5h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z"/>
            </svg>
          </div>
          <h1>Streamline your IT support</h1>
          <p>The next-generation ticketing system for modern teams.</p>
          <div class="feature-list">
            <div class="feature-item">
              <span class="dot"></span>
              Real-time collaboration
            </div>
            <div class="feature-item">
              <span class="dot"></span>
              Visual Kanban boards
            </div>
            <div class="feature-item">
              <span class="dot"></span>
              Advanced file tracking
            </div>
          </div>
        </div>
        <div class="sidebar-footer">
          © 2026 IT Ticketing System. All rights reserved.
        </div>
      </div>
      
      <div class="login-form-section">
        <div class="form-card">
          <div class="form-header">
            <h2>Log in</h2>
            <p>Welcome back! Please enter your details.</p>
          </div>
          
          <form @submit.prevent="handleLogin">
            <div class="input-group">
              <label>Username</label>
              <input 
                type="text" 
                v-model="username" 
                placeholder="e.g. admin"
                required
                :class="{ 'error-border': error }"
              />
            </div>
            
            <div class="input-group">
              <label>Password</label>
              <input 
                type="password" 
                v-model="password" 
                placeholder="••••••••"
                required
                :class="{ 'error-border': error }"
              />
            </div>

            <div class="form-options">
              <label class="checkbox-container">
                <input type="checkbox">
                <span class="checkmark"></span>
                Remember for 30 days
              </label>
              <router-link to="/forgot-password" class="forgot-link">Forgot password?</router-link>
            </div>
            
            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="!loading">Sign in</span>
              <div v-else class="spinner"></div>
            </button>
            
            <Transition name="slide-up">
              <div v-if="error" class="error-alert">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {{ error }}
              </div>
            </Transition>
          </form>
          
          <div class="form-footer">
            <p>Don't have an account? <router-link to="/request-access">Request access</router-link></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useRouter } from 'vue-router'

const username = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

const authStore = useAuthStore()
const router = useRouter()

const handleLogin = async () => {
  loading.value = true
  error.value = ''
  try {
    await authStore.login(username.value, password.value)
    router.push({ name: 'dashboard' })
  } catch (err: any) {
    console.error('Login Error details:', err)
    if (!err.response) {
      const attemptedUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000')
      error.value = `Network error. Tidak bisa terhubung ke: ${attemptedUrl}. Pastikan Backend aktif.`
    } else {
      error.value = 'Invalid username or password'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  width: 100%;
  height: 100vh;
  display: flex;
  background-color: #fff;
}

.login-container {
  display: flex;
  width: 100%;
  height: 100%;
}

.login-sidebar {
  flex: 1;
  background: linear-gradient(160deg, #0052cc 0%, #0747a6 100%);
  color: white;
  padding: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.login-sidebar::before {
  content: '';
  position: absolute;
  top: -10%;
  right: -10%;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
}

.sidebar-content {
  max-width: 440px;
  z-index: 1;
}

.app-logo {
  margin-bottom: 3rem;
  color: white;
}

.sidebar-content h1 {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: white;
}

.sidebar-content p {
  font-size: 1.2rem;
  opacity: 0.8;
  margin-bottom: 3rem;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-weight: 500;
  font-size: 1.1rem;
}

.dot {
  width: 8px;
  height: 8px;
  background-color: #4c9aff;
  border-radius: 50%;
}

.sidebar-footer {
  font-size: 0.9rem;
  opacity: 0.6;
}

.login-form-section {
  flex: 1.2;
  background-color: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.form-card {
  width: 100%;
  max-width: 420px;
}

.form-header {
  margin-bottom: 2.5rem;
}

.form-header h2 {
  font-size: 2rem;
  color: #172b4d;
  margin-bottom: 0.5rem;
}

.form-header p {
  color: #5e6c84;
  font-size: 1rem;
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e6c84;
  margin-bottom: 0.5rem;
}

.input-group input {
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  font-size: 1rem;
  color: #172b4d !important; /* FIX: Visibility */
  background-color: white;
  transition: all 0.2s ease;
}

.input-group input:focus {
  outline: none;
  border-color: #0052cc;
  box-shadow: 0 0 0 4px rgba(0, 82, 204, 0.1);
}

.input-group input.error-border {
  border-color: #de350b;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #5e6c84;
}

.forgot-link {
  color: #0052cc;
  font-weight: 600;
}

.submit-btn {
  width: 100%;
  padding: 0.9rem;
  background-color: #0052cc;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-btn:hover {
  background-color: #0065ff;
}

.submit-btn:disabled {
  background-color: #ebecf0;
  color: #a5adba;
  cursor: not-allowed;
}

.error-alert {
  margin-top: 1.5rem;
  background-color: #ffebe6;
  color: #de350b;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.form-footer {
  margin-top: 2.5rem;
  text-align: center;
  font-size: 0.95rem;
  color: #5e6c84;
}

.form-footer a {
  color: #0052cc;
  font-weight: 600;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.slide-up-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .login-sidebar {
    display: none;
  }
}
</style>
